import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { COLORS, SharedMapObjectMap, WEHUNT_APP_STORE_URL, type SharedMapObject } from '@wehunt/ui'
import type { MapLineLink, MapPinLink } from './mapLinkPayload'

export type MapLinkObject = { kind: 'pin'; pin: MapPinLink } | { kind: 'line'; line: MapLineLink }

export interface MapLinkViewProps {
  object: MapLinkObject
  /**
   * The way onto the team's own map, for a signed-in member of the team the
   * object belongs to. The shell supplies it — campfire cannot know clubhouse's
   * routes or who is in which team — and it renders nothing for anyone else.
   */
  renderTeamLink?: (groupId: string) => ReactNode
}

function mapObjectOf(object: MapLinkObject): SharedMapObject {
  if (object.kind === 'pin') {
    const { pin } = object
    return {
      kind: 'pin',
      pin: {
        type: pin.type,
        lat: pin.lat,
        lon: pin.lon,
        label: pin.name,
        color: pin.color,
        customSymbol: pin.customSymbol,
      },
    }
  }
  const { line } = object
  return {
    kind: 'line',
    line: {
      name: line.name,
      kind: line.kind,
      points: line.points,
      color: line.color,
      style: line.style,
      width: line.width,
    },
  }
}

function titleKeyOf(object: MapLinkObject) {
  if (object.kind === 'line') return 'mapLink.lineTitle'
  return object.pin.kind === 'location' ? 'mapLink.locationTitle' : 'mapLink.pinTitle'
}

/**
 * The public page a shared map pin, dropped location or line opens on: the
 * object on the map, what it is, and the way into WeHunt.
 *
 * Signed out is the expected case — the link was sent to someone, often someone
 * without the app — so nothing here needs a session; the team link is the one
 * extra a signed-in member gets.
 */
export function MapLinkView({ object, renderTeamLink }: Readonly<MapLinkViewProps>) {
  const { t } = useTranslation('replay')
  const title = t(titleKeyOf(object))
  // The app lets a shared object's name be empty; the heading then says what it is.
  const name = (object.kind === 'line' ? object.line.name : object.pin.name) || title
  const groupId = object.kind === 'line' ? object.line.groupId : object.pin.groupId

  return (
    <main
      data-testid="map-link-view"
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: COLORS.surface }}
    >
      <SharedMapObjectMap object={mapObjectOf(object)} label={t('mapLink.mapAlt')} />
      <section
        aria-labelledby="map-link-name"
        style={{
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 16,
          maxWidth: 420,
          margin: '0 auto',
          padding: 16,
          borderRadius: 16,
          background: COLORS.surfaceContainerHigh,
          color: COLORS.onSurface,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: COLORS.onSurfaceVariant }}>{title}</p>
        <h1
          id="map-link-name"
          style={{
            margin: 0,
            fontFamily: COLORS.fontDisplay,
            fontSize: 20,
            fontWeight: 500,
            overflowWrap: 'anywhere',
          }}
        >
          {name}
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: COLORS.onSurfaceVariant }}>{t('mapLink.sharedVia')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <a className="wh-btn wh-btn-primary" href={WEHUNT_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            {t('mapLink.downloadApp')}
          </a>
          {renderTeamLink?.(groupId)}
        </div>
      </section>
    </main>
  )
}
